import { zodResolver } from '@hookform/resolvers/zod';
import {
  configMgmtControllerCreateRequestType,
  configMgmtControllerListRequestTypes,
} from '@kalba/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { Eye, FileCheck, FileText, Plus, RefreshCw, Search, X } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { AppLayout } from '../../../components/layout/app-layout';
import { PageHeader } from '../../../components/layout/page-header';
import {
  CardDataTable,
  type CardDataTableColumn,
  type CardDataTableRowDef,
} from '../../../components/shared/card-data-table';
import { StatCard } from '../../../components/shared/stat-card';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { usePermissions } from '../../../hooks/use-permissions';
import { cn } from '../../../lib/utils';
import { useAuthStore } from '../../../stores/auth.store';
import { usePermitTypesListStore } from '../../../stores/config/permit-types-list.store';

const DOMAIN_OPTIONS = [
  { value: 'PERMIT', label: 'Permit' },
  { value: 'STUDY_REQUEST', label: 'Study Request' },
];

const LICENSING_AUTHORITY_OPTIONS = [
  { value: 'KALBA_MUNICIPALITY', label: 'Kalba Municipality' },
  { value: 'SHARJAH_MUNICIPALITY', label: 'Sharjah Municipality' },
];

const createPermitTypeSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  code: z.string().min(1, 'Code is required').max(10),
  description: z.string().optional(),
  hasInspection: z.boolean(),
  inspectionMandatory: z.boolean(),
  hasPledgeStudy: z.boolean(),
  hasRenewal: z.boolean(),
  domain: z.enum(['PERMIT', 'STUDY_REQUEST']),
  licensingAuthority: z.enum(['KALBA_MUNICIPALITY', 'SHARJAH_MUNICIPALITY']),
});

// ── Types ─────────────────────────────────────────────────────────────────────

interface PermitTypeSummary {
  id: string;
  name: string;
  code: string;
  description?: string | null;
  hasInspection: boolean;
  hasPledgeStudy: boolean;
  hasRenewal: boolean;
  status: string;
  domain: string;
  licensingAuthority: string;
  _count: { riskLevels: number; feeComponents: number; documentTypeLinks: number };
}

// ── Route ─────────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/config/request-types/')({
  beforeLoad: () => {
    const { user, permissions } = useAuthStore.getState();
    if (!user) throw redirect({ to: '/login' });
    if (!permissions.includes('config:view')) throw redirect({ to: '/dashboard' });
  },
  component: PermitTypesPage,
});

// ── Helpers ───────────────────────────────────────────────────────────────────

function PermitTypeAvatar({ name }: { name: string }) {
  const colors = [
    'from-blue-500 to-indigo-600',
    'from-emerald-500 to-teal-600',
    'from-amber-500 to-orange-600',
    'from-violet-500 to-purple-600',
    'from-sky-500 to-cyan-600',
    'from-rose-500 to-pink-600',
  ];
  const idx = (name.charCodeAt(0) + name.length) % colors.length;
  return (
    <div
      className={cn(
        'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br text-white shadow-sm',
        colors[idx],
      )}
    >
      <FileCheck size={18} />
    </div>
  );
}

function FeatureBadge({ label, color }: { label: string; color: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold',
        color,
      )}
    >
      {label}
    </span>
  );
}

function CheckboxToggle({
  id,
  label,
  checked,
  onChange,
  disabled,
}: {
  id: string;
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label
      htmlFor={id}
      className={cn(
        'flex items-center gap-3 cursor-pointer rounded-xl border p-3 transition-all',
        checked ? 'border-brand-300 bg-brand-50' : 'border-gray-200 bg-white hover:bg-gray-50',
        disabled && 'opacity-50 cursor-not-allowed',
      )}
    >
      <input
        type="checkbox"
        id={id}
        className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
      />
      <span className="text-sm font-medium text-gray-700">{label}</span>
    </label>
  );
}

// ── List columns ──────────────────────────────────────────────────────────────

type SortKey = 'name-asc' | 'name-desc' | 'docs-desc' | 'fees-desc';

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'name-asc', label: 'Name A–Z' },
  { value: 'name-desc', label: 'Name Z–A' },
  { value: 'docs-desc', label: 'Most Documents' },
  { value: 'fees-desc', label: 'Most Fee Components' },
];

function sortTypes(list: PermitTypeSummary[], key: SortKey): PermitTypeSummary[] {
  return [...list].sort((a, b) => {
    if (key === 'name-asc') return a.name.localeCompare(b.name);
    if (key === 'name-desc') return b.name.localeCompare(a.name);
    if (key === 'docs-desc') return b._count.documentTypeLinks - a._count.documentTypeLinks;
    if (key === 'fees-desc') return b._count.feeComponents - a._count.feeComponents;
    return 0;
  });
}

const PT_COLUMNS: CardDataTableColumn[] = [
  { id: 'requestType', header: 'Request Type' },
  { id: 'domain', header: 'Domain' },
  { id: 'features', header: 'Features' },
  { id: 'counts', header: 'Config', align: 'center' },
  { id: 'status', header: 'Status', align: 'center' },
];

const PT_GRID = 'minmax(0,1fr) 160px 220px 160px 100px';

// ── Page ──────────────────────────────────────────────────────────────────────

function PermitTypesPage() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const qc = useQueryClient();
  const canEdit = can('config:edit');

  const {
    search,
    debounced,
    statusFilter,
    sort,
    page,
    createOpen,
    createError,
    setSearch,
    setDebounced,
    setStatusFilter,
    setSort,
    setPage,
    setCreateOpen,
    setCreateError,
  } = usePermitTypesListStore();
  const PAGE_SIZE = 10;
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset: resetForm,
    formState: { errors: formErrors },
  } = useForm<z.infer<typeof createPermitTypeSchema>>({
    resolver: zodResolver(createPermitTypeSchema),
    defaultValues: {
      name: '',
      code: '',
      description: '',
      hasInspection: false,
      inspectionMandatory: false,
      hasPledgeStudy: false,
      hasRenewal: true,
      domain: 'PERMIT' as const,
      licensingAuthority: 'KALBA_MUNICIPALITY' as const,
    },
  });

  useEffect(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setDebounced(search), 250);
    return () => clearTimeout(timerRef.current);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debounced, statusFilter, sort]);

  const {
    data: rawTypes,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['requestTypes'],
    queryFn: () =>
      configMgmtControllerListRequestTypes() as unknown as Promise<PermitTypeSummary[]>,
  });

  const createMutation = useMutation({
    mutationFn: (data: z.infer<typeof createPermitTypeSchema>) =>
      configMgmtControllerCreateRequestType({
        requestBody: {
          name: data.name.trim(),
          code: data.code.trim().toUpperCase(),
          description: data.description?.trim() || undefined,
          hasInspection: data.hasInspection,
          inspectionMandatory: data.inspectionMandatory,
          hasPledgeStudy: data.hasPledgeStudy,
          hasRenewal: data.hasRenewal,
          domain: data.domain as 'PERMIT' | 'STUDY_REQUEST',
          licensingAuthority: data.licensingAuthority as
            | 'KALBA_MUNICIPALITY'
            | 'SHARJAH_MUNICIPALITY',
        },
      }),
    onSuccess: (created: unknown) => {
      qc.invalidateQueries({ queryKey: ['requestTypes'] });
      closeCreate();
      const id = (created as { id: string })?.id;
      if (id)
        navigate({ to: '/config/request-types/$requestTypeId', params: { requestTypeId: id } });
    },
    onError: async (err: unknown) => {
      try {
        const body = (await (err as { response?: Response }).response?.json()) as
          | { message?: string }
          | undefined;
        setCreateError(body?.message ?? 'Create failed');
      } catch {
        setCreateError('Create failed');
      }
    },
  });

  function closeCreate() {
    setCreateOpen(false);
    resetForm();
    setCreateError(null);
  }

  const types = rawTypes ?? [];

  // Stats
  const totalTypes = types.length;
  const activeCount = types.filter((t) => t.status === 'ACTIVE').length;
  const inspectionCount = types.filter((t) => t.hasInspection).length;
  const renewalCount = types.filter((t) => t.hasRenewal).length;

  // Filter + sort
  const filtered = sortTypes(
    types.filter((t) => {
      if (debounced) {
        const q = debounced.toLowerCase();
        if (!t.name.toLowerCase().includes(q) && !t.code.toLowerCase().includes(q)) return false;
      }
      if (statusFilter === 'active' && t.status !== 'ACTIVE') return false;
      if (statusFilter === 'inactive' && t.status === 'ACTIVE') return false;
      return true;
    }),
    sort,
  );

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <AppLayout>
      <PageHeader
        title="Request Types"
        description="Configure request type definitions, domain routing, and associated settings."
        actions={
          canEdit ? (
            <Button variant="success" onClick={() => setCreateOpen(true)}>
              <Plus size={16} />
              New Request Type
            </Button>
          ) : null
        }
      />

      {/* Stat cards */}
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          icon={<FileCheck size={18} />}
          iconBg="bg-blue-50"
          iconColor="text-blue-500"
          delta={`${activeCount} active · ${totalTypes - activeCount} inactive`}
          value={totalTypes}
          label="Total Types"
          loading={isLoading}
        />
        <StatCard
          icon={<FileText size={18} />}
          iconBg="bg-emerald-50"
          iconColor="text-emerald-500"
          delta="Currently enabled"
          value={activeCount}
          label="Active Types"
          loading={isLoading}
        />
        <StatCard
          icon={<Eye size={18} />}
          iconBg="bg-orange-50"
          iconColor="text-orange-500"
          delta="Require site visit"
          value={inspectionCount}
          label="With Inspection"
          loading={isLoading}
        />
        <StatCard
          icon={<RefreshCw size={18} />}
          iconBg="bg-sky-50"
          iconColor="text-sky-500"
          delta="Support renewal flow"
          value={renewalCount}
          label="With Renewal"
          loading={isLoading}
        />
      </div>

      {/* Toolbar */}
      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-gray-200 bg-white p-3 shadow-sm">
        <div className="relative min-w-60 flex-1">
          <Search
            size={14}
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or code…"
            className="h-9 w-full rounded-xl border border-gray-200 bg-gray-50 pl-10 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-brand-100 focus:border-brand-400 transition-all placeholder:text-gray-400"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X size={13} />
            </button>
          )}
        </div>

        {/* Status filter pills */}
        <div className="flex items-center gap-1 rounded-full bg-gray-100 p-1">
          {(['all', 'active', 'inactive'] as const).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setStatusFilter(v)}
              className={cn(
                'rounded-full px-3 py-1 text-xs font-medium transition-all',
                statusFilter === v
                  ? 'bg-success-500 text-white shadow-sm'
                  : 'text-gray-500 hover:text-gray-700',
              )}
            >
              {v === 'all' ? 'All' : v.charAt(0).toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>

        {/* Sort dropdown */}
        <Select value={sort} onValueChange={(v) => setSort(v as SortKey)}>
          <SelectTrigger className="h-9 w-44 rounded-xl">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SORT_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <span className="ml-auto text-xs text-gray-400">
          {filtered.length} of {totalTypes} types
        </span>
      </div>

      {/* List */}
      <CardDataTable
        gridTemplateColumns={PT_GRID}
        columns={PT_COLUMNS}
        isLoading={isLoading}
        errorContent={
          isError ? (
            <div className="rounded-2xl border border-error-200 bg-error-50 p-8 text-center">
              <p className="text-sm font-medium text-error-600">Failed to load permit types</p>
            </div>
          ) : undefined
        }
        emptyContent={
          <div className="rounded-2xl border border-gray-200 bg-white p-16 text-center shadow-sm">
            <FileCheck size={40} className="mx-auto mb-4 text-gray-200" />
            <p className="text-sm font-semibold text-gray-500">No request types found</p>
            <p className="mt-1 text-xs text-gray-400">Try adjusting your search or filters</p>
          </div>
        }
        rows={paginated.map(
          (pt): CardDataTableRowDef => ({
            id: pt.id,
            avatar: <PermitTypeAvatar name={pt.name} />,
            cells: {
              requestType: (
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="truncate text-sm font-semibold text-gray-900 group-hover:text-brand-600 transition-colors leading-snug">
                      {pt.name}
                    </p>
                    <span className="shrink-0 rounded-md border border-gray-200 bg-gray-50 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-gray-500">
                      {pt.code}
                    </span>
                  </div>
                  <p className="truncate text-xs text-gray-400">{pt.description ?? '—'}</p>
                </div>
              ),
              domain: (
                <div className="min-w-0 space-y-1">
                  <span
                    className={cn(
                      'inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold',
                      pt.domain === 'STUDY_REQUEST'
                        ? 'border-violet-200 bg-violet-50 text-violet-700'
                        : 'border-blue-200 bg-blue-50 text-blue-700',
                    )}
                  >
                    {pt.domain === 'STUDY_REQUEST' ? 'Study Request' : 'Permit'}
                  </span>
                  <p className="text-[10px] text-gray-400 truncate">
                    {pt.licensingAuthority === 'SHARJAH_MUNICIPALITY'
                      ? 'Sharjah Municipality'
                      : 'Kalba Municipality'}
                  </p>
                </div>
              ),
              features: (
                <div className="flex flex-wrap items-center gap-1">
                  {pt.hasInspection && (
                    <FeatureBadge
                      label="Inspection"
                      color="border-orange-200 bg-orange-50 text-orange-700"
                    />
                  )}
                  {pt.hasPledgeStudy && (
                    <FeatureBadge
                      label="Pledge/Study"
                      color="border-violet-200 bg-violet-50 text-violet-700"
                    />
                  )}
                  {pt.hasRenewal && (
                    <FeatureBadge label="Renewal" color="border-sky-200 bg-sky-50 text-sky-700" />
                  )}
                  {!pt.hasInspection && !pt.hasPledgeStudy && !pt.hasRenewal && (
                    <span className="text-xs text-gray-300">—</span>
                  )}
                </div>
              ),
              counts: (
                <div className="flex items-center justify-center gap-3 text-xs text-gray-500">
                  <span title="Risk Categories" className="flex flex-col items-center gap-0.5">
                    <span className="text-sm font-bold text-gray-800">{pt._count.riskLevels}</span>
                    <span>categories</span>
                  </span>
                  <span className="text-gray-200">|</span>
                  <span title="Document Types" className="flex flex-col items-center gap-0.5">
                    <span className="text-sm font-bold text-gray-800">
                      {pt._count.documentTypeLinks}
                    </span>
                    <span>docs</span>
                  </span>
                </div>
              ),
              status: (
                <span
                  className={cn(
                    'inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold',
                    pt.status === 'ACTIVE'
                      ? 'bg-success-50 text-success-700 border border-success-200'
                      : 'bg-gray-100 text-gray-500 border border-gray-200',
                  )}
                >
                  {pt.status === 'ACTIVE' ? 'Active' : 'Inactive'}
                </span>
              ),
            },
            onClick: () =>
              navigate({
                to: '/config/request-types/$requestTypeId',
                params: { requestTypeId: pt.id },
              }),
          }),
        )}
      />

      {/* Pagination */}
      {!isLoading && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Showing {paginated.length} of {filtered.length} request types
          </p>
          {totalPages > 1 && (
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPage(p)}
                  className={cn(
                    'flex h-8 w-8 items-center justify-center rounded-full text-xs font-medium transition-colors',
                    page === p
                      ? 'bg-success-500 text-white shadow-sm'
                      : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800',
                  )}
                >
                  {p}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Create dialog */}
      <Dialog
        open={createOpen}
        onOpenChange={(v) => {
          if (!v) closeCreate();
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>New Request Type</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit((d) => createMutation.mutate(d))}>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="mb-1.5 block text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    Name <span className="text-error-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Environmental Permit"
                    className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-100 focus:border-brand-400 transition-all"
                    {...register('name')}
                  />
                  {formErrors.name && (
                    <p className="mt-1 text-xs text-error-500">{formErrors.name.message}</p>
                  )}
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    Code <span className="text-error-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="ENV"
                    maxLength={10}
                    className="w-full rounded-xl border border-gray-200 px-3 py-2.5 font-mono text-sm uppercase focus:outline-none focus:ring-2 focus:ring-brand-100 focus:border-brand-400 transition-all"
                    {...register('code', {
                      onChange: (e) => {
                        e.target.value = e.target.value.toUpperCase();
                      },
                    })}
                  />
                  {formErrors.code && (
                    <p className="mt-1 text-xs text-error-500">{formErrors.code.message}</p>
                  )}
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    Description
                  </label>
                  <input
                    type="text"
                    placeholder="Optional"
                    className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-100 focus:border-brand-400 transition-all"
                    {...register('description')}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    Domain <span className="text-error-500">*</span>
                  </label>
                  <Select
                    value={watch('domain')}
                    onValueChange={(v) => setValue('domain', v as 'PERMIT' | 'STUDY_REQUEST')}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select domain…" />
                    </SelectTrigger>
                    <SelectContent>
                      {DOMAIN_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    Licensing Authority <span className="text-error-500">*</span>
                  </label>
                  <Select
                    value={watch('licensingAuthority')}
                    onValueChange={(v) =>
                      setValue(
                        'licensingAuthority',
                        v as 'KALBA_MUNICIPALITY' | 'SHARJAH_MUNICIPALITY',
                      )
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select authority…" />
                    </SelectTrigger>
                    <SelectContent>
                      {LICENSING_AUTHORITY_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <p className="mb-2.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Feature Flags
                </p>
                <div className="space-y-2">
                  <CheckboxToggle
                    id="hasInspection"
                    label="Requires Inspection"
                    checked={watch('hasInspection')}
                    onChange={(v) => {
                      setValue('hasInspection', v);
                      if (!v) setValue('inspectionMandatory', false);
                    }}
                  />
                  {watch('hasInspection') && (
                    <div className="ml-4">
                      <CheckboxToggle
                        id="inspectionMandatory"
                        label="Inspection Mandatory"
                        checked={watch('inspectionMandatory')}
                        onChange={(v) => setValue('inspectionMandatory', v)}
                      />
                    </div>
                  )}
                  <CheckboxToggle
                    id="hasPledgeStudy"
                    label="Requires Pledge / Study"
                    checked={watch('hasPledgeStudy')}
                    onChange={(v) => setValue('hasPledgeStudy', v)}
                  />
                  <CheckboxToggle
                    id="hasRenewal"
                    label="Supports Renewal"
                    checked={watch('hasRenewal')}
                    onChange={(v) => setValue('hasRenewal', v)}
                  />
                </div>
              </div>

              {createError && (
                <p className="rounded-xl border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-600">
                  {createError}
                </p>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={closeCreate}
                disabled={createMutation.isPending}
              >
                Cancel
              </Button>
              <Button variant="success" size="sm" type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Creating…' : 'Create Request Type'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
